import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'vi-ant-design:dashboard-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.analysis'),
          noCache: true,
          affix: true
        }
      },
      {
        path: 'workplace',
        component: () => import('@/views/Dashboard/Workplace.vue'),
        name: 'Workplace',
        meta: {
          title: t('router.workplace'),
          noCache: true
        }
      }
    ]
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'vi-ant-design:dashboard-filled',
      alwaysShow: true
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.analysis'),
          noCache: true,
          affix: true
        }
      },
      {
        path: 'workplace',
        component: () => import('@/views/Dashboard/Workplace.vue'),
        name: 'Workplace',
        meta: {
          title: t('router.workplace'),
          noCache: true
        }
      }
    ]
  },
  {
    path: '/gameManage/tools',
    component: Layout,
    name: 'Tools',
    meta: {
      title: '工具管理',
      icon: 'vi-bx:bxs-component',
      alwaysShow: true
    },
    children: [
      {
        path: 'index',
        component: () => import('@/views/GameLibrary/toolsList.vue'),
        name: 'ToolsList',
        meta: {
          title: '工具列表',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'toolsDetails/:id', // 游戏工具(图鉴)二级列表页
        component: () => import('@/views/GameLibrary/toolsDetails.vue'),
        name: 'ToolsDetails',
        meta: {
          title: '图鉴列表',
          icon: 'vi-cib:telegram-plane',
          hidden: true
        }
      },
      {
        path: 'mapDetails/:id', // 游戏工具(地图)二级列表页
        component: () => import('@/views/GameLibrary/mapDetails.vue'),
        name: 'mapDetails',
        meta: {
          title: '地图列表',
          icon: 'vi-cib:telegram-plane',
          hidden: true
        }
      },
      {
        path: 'calculatorDetails/:id', // 游戏工具(计算器)二级列表页
        component: () => import('@/views/GameLibrary/calculatorDetails.vue'),
        name: 'calculatorDetails',
        meta: {
          title: '计算器列表',
          icon: 'vi-cib:telegram-plane',
          hidden: true
        }
      }
    ]
  },
  {
    path: '/userManage',
    component: Layout,
    name: 'userManageList',
    meta: {
      title: '用户管理',
      icon: 'vi-bx:bxs-component',
      alwaysShow: true
    },
    children: [
      {
        path: 'index',
        component: () => import('@/views/UserManagement/index.vue'),
        name: 'userListManage',
        meta: {
          title: '用户列表',
          icon: 'vi-cib:telegram-plane'
        }
      }
    ]
  },
  {
    path: '/gameManager',
    component: Layout,
    name: 'gameManager',
    meta: {
      title: '游戏管理',
      icon: 'vi-bx:bxs-component',
      alwaysShow: true
    },
    children: [
      {
        path: 'manualTask',
        component: () => import('@/views/GameManager/manualTask.vue'),
        name: 'manualTask',
        meta: {
          title: '手动任务',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'gameListManage',
        component: () => import('@/views/GameManager/gameListManage.vue'),
        name: 'gameListManage',
        meta: {
          title: '游戏列表',
          icon: 'vi-cib:telegram-plane'
        }
      }
    ]
  },
  {
    path: '/eventTracking',
    component: Layout,
    name: 'eventTracking',
    meta: {
      title: '埋点统计',
      icon: 'vi-bx:bxs-component',
      alwaysShow: true
    },
    children: [
      {
        path: 'contentRank',
        component: () => import('@/views/EventTracking/contentRank.vue'),
        name: 'contentRank',
        meta: {
          title: '内容排行榜报表',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'content',
        component: () => import('@/views/EventTracking/content.vue'),
        name: 'content',
        meta: {
          title: '内容报表',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'user',
        component: () => import('@/views/EventTracking/user.vue'),
        name: 'eventTrackingUser',
        meta: {
          title: '用户报表',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'gamePlatformBind',
        component: () => import('@/views/EventTracking/gamePlatformBind.vue'),
        name: 'gamePlatformBind',
        meta: {
          title: '游戏平台绑定报表',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'gameList',
        component: () => import('@/views/EventTracking/gameList.vue'),
        name: 'eventTrackingGameList',
        meta: {
          title: '游戏清单报表',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'homeNav',
        component: () => import('@/views/EventTracking/homeNav.vue'),
        name: 'eventTrackingHomeNav',
        meta: {
          title: '金刚位报表',
          icon: 'vi-cib:telegram-plane'
        }
      },
      {
        path: 'gameLibrary',
        component: () => import('@/views/EventTracking/gameLibrary.vue'),
        name: 'eventTrackingGameLibrary',
        meta: {
          title: '游戏库报表',
          icon: 'vi-cib:telegram-plane'
        }
      }
    ]
  },
  {
    path: '/authorization',
    component: Layout,
    redirect: '/authorization/user',
    name: 'Authorization',
    meta: {
      title: t('router.authorization'),
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true
    },
    children: [
      // {
      //   path: 'department',
      //   component: () => import('@/views/Authorization/Department/Department.vue'),
      //   name: 'Department',
      //   meta: {
      //     title: t('router.department')
      //   }
      // },
      {
        path: 'user',
        component: () => import('@/views/Authorization/User/User.vue'),
        name: 'User',
        meta: {
          title: t('router.user')
        }
      },
      {
        path: 'menu',
        component: () => import('@/views/Authorization/Menu/Menu.vue'),
        name: 'Menu',
        meta: {
          title: t('router.menuManagement')
        }
      },
      {
        path: 'role',
        component: () => import('@/views/Authorization/Role/Role.vue'),
        name: 'Role',
        meta: {
          title: t('router.role')
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
